import MuiAutoComplete from "@mui/material/Autocomplete";
import type {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MuiTextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { get } from "lodash";
import { useFormikContext } from "formik";
import { useEffect } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { v4 } from "uuid";
import { useFormContext } from "@/hooks/useFormContext";
import { useDynamicTranslation } from "@/hooks/useDynamicTranslation";
import { FormSkeleton } from "../../skeleton";

type FormValues = Record<string, unknown>;

export interface AutoCompleteOption {
  value: unknown;
  label: string;
  [key: string]: unknown;
}

type MuiAutoCompleteProps = AutocompleteProps<AutoCompleteOption, false, false, false>;

export interface AutoCompleteProps {
  name?: string;
  label?: string;
  options: AutoCompleteOption[];
  value?: unknown;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: AutoCompleteOption,
    state: AutocompleteRenderOptionState
  ) => ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
  getOptionLabel?: MuiAutoCompleteProps["getOptionLabel"];
  isOptionEqualToValue?: MuiAutoCompleteProps["isOptionEqualToValue"];
  loading?: boolean;
  onChange?: MuiAutoCompleteProps["onChange"];
  error?: string;
}

export function AutoComplete({
  name,
  label,
  options,
  value,
  renderOption,
  renderInput,
  getOptionLabel,
  isOptionEqualToValue,
  loading,
  onChange,
  error,
}: AutoCompleteProps) {
  const { t } = useDynamicTranslation();
  const formik = useFormikContext<FormValues>();
  const formContext = useFormContext();
  const fieldName = name as string;

  useEffect(() => {
    formik.setFieldError(fieldName, error);
  }, [error]);

  let labelId: string | undefined;
  let errorLabelId: string | undefined;

  const handleChange: MuiAutoCompleteProps["onChange"] = (_event, selected) => {
    if (selected == null) {
      formik.setFieldValue(fieldName, null, false);
      formik.setFieldError(fieldName, t("validations:mixed.required"));

      return;
    }

    formik.setFieldValue(fieldName, selected.value);
  };

  const defaultGetOptionLabel = (selectedValue: AutoCompleteOption) =>
    options?.find((o) => o.value === selectedValue)?.label ?? "";
  const defaultFilterOptions = (opts: AutoCompleteOption[], state: { inputValue: string }) =>
    opts.filter((o) => o.label.includes(state.inputValue));

  useEffect(() => {
    labelId = v4();
    errorLabelId = v4();
  }, []);

  const defaultRenderInput = (params: AutocompleteRenderInputParams) => (
    <MuiTextField {...params} id={labelId} label={label} />
  );

  const defaultRenderOption = (
    params: HTMLAttributes<HTMLLIElement>,
    option: AutoCompleteOption
  ) => <Typography {...params}>{option.label}</Typography>;

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <FormControl
        sx={{ width: "100%" }}
        error={get(formik?.touched, fieldName) && Boolean(get(formik.errors, fieldName))}
      >
        <MuiAutoComplete
          value={(value ?? get(formik?.values, fieldName, "")) as AutoCompleteOption}
          isOptionEqualToValue={
            isOptionEqualToValue ||
            ((option, selectedValue) => option.value === (selectedValue as unknown))
          }
          getOptionLabel={getOptionLabel || defaultGetOptionLabel}
          filterOptions={defaultFilterOptions}
          options={options}
          renderOption={renderOption ?? defaultRenderOption}
          renderInput={renderInput ?? defaultRenderInput}
          onChange={onChange ?? handleChange}
          sx={{
            ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
              width: "auto",
            },
          }}
        />
        {get(formik?.touched, fieldName) && Boolean(get(formik.errors, fieldName)) && (
          <FormHelperText id={errorLabelId}>
            {get(formik.errors, fieldName) as ReactNode}
          </FormHelperText>
        )}
      </FormControl>
    </FormSkeleton>
  );
}
